import { IIntegration, IntegrationType } from '@principle-theorem/integrations';
import { DocumentReference, getEnumValues } from '@principle-theorem/shared';
import { IOrganisation } from '../organisation/organisation';
import { IPatient } from '../patient/patient';
import { IPatientPrescriptionSummary } from '../prescription/prescription';

export function isParchmentIntegration(
  item: IIntegration
): item is IIntegration<IParchmentIntegrationData> {
  return item.type === IntegrationType.Parchment;
}

export interface IParchmentIntegrationData {
  organisationId: string;
  organisationSecret: string;
  userId: string;
  accessToken?: IParchmentTokenApiResponse;
}

export interface IParchmentTokenApiResponse {
  accessToken: string;
  expiresIn: number;
  tokenType: string;
  scope: ParchmentScope[];
}

export interface IParchmentTokenApiRequest {
  grantType: 'client_credentials';
  scope: ParchmentScope[];
}

export enum ParchmentScope {
  PatientCreate = 'create:patient',
  PatientRead = 'read:patient',
  PatientUpdate = 'update:patient',
  PatientDelete = 'delete:patient',
  PatientPrescriptionRead = 'read:patient_prescription',
  UserCreate = 'create:user',
  UserRead = 'read:user',
  UserUpdate = 'update:user',
  UserDelete = 'delete:user',
}

export const PARCHMENT_SCOPES = getEnumValues(ParchmentScope);

export interface IParchmentCreatePatientApiResponse {
  /**
   * Partner's external identifier for the patient
   */
  external_patient_id: string; // 'PARTNER#12345';

  /**
   * Parchment's unique identifier for the patient
   */
  parchment_patient_id: string; // 'pat_abc123def456';

  /**
   * URL to access the patient in Parchment portal
   */
  url: string; // 'https://app.parchment.health/dashboard/patients/pat_abc123def456';
}

export interface IParchmentCreatePatient {
  organisationRef: DocumentReference<IOrganisation>;
  patientRef: DocumentReference<IPatient>;
}

export interface IParchmentCreatePatientSuccess {
  status: 'success';
  message: string;
}

export interface IParchmentCreatePatientError {
  status: string;
  message: string;
  details?: unknown;
}

export type IParchmentCreatePatientResponse =
  | IParchmentCreatePatientSuccess
  | IParchmentCreatePatientError;

export type ParchmentAddressState =
  | 'NSW'
  | 'VIC'
  | 'QLD'
  | 'WA'
  | 'SA'
  | 'TAS'
  | 'ACT'
  | 'NT';

export interface IParchmentCreatePatientRequest {
  /**
   * Patient's given name
   */
  given_name: string;

  /**
   * Patient's family name
   */
  family_name: string;

  /**
   * Patient's date of birth in YYYY-MM-DD format
   */
  date_of_birth: string;

  /**
   * Patient's sex
   */
  sex: 'M' | 'F' | 'I' | 'N';

  /**
   * Patient's email address
   */
  email: string;

  /**
   * Patient's phone number
   */
  phone: string;

  /**
   * Partner's identifier
   */
  partner_id: string;

  /**
   * Partner's unique identifier for the patient
   */
  partner_patient_id: string;

  /**
   * DVA file number
   */
  dva_file_number?: string;

  /**
   * Medicare card number
   */
  medicare_card_number?: string;

  /**
   * Medicare IRN
   */
  medicare_irn?: string;

  /**
   * IHI number (Individual Healthcare Identifier)
   */
  ihi_number?: string;

  australian_street_address?: {
    /**
     * Street number
     */
    street_number: string;

    /**
     * Street name
     */
    street_name: string;

    /**
     * Suburb
     */
    suburb: string;

    /**
     * Australian state or territory
     */
    state: ParchmentAddressState;

    /**
     * Postcode
     */
    postcode: string;
  };

  /**
   * Medicare card expiry date
   */
  medicare_valid_to?: string;

  /**
   * Concession or pension number
   */
  concession_pension_number?: string;

  /**
   * Entitlement number
   */
  entitlement_number?: string;

  /**
   * DVA card color
   */
  dva_card_color?: 'gold' | 'white' | 'orange';

  /**
   * RACF identifier
   */
  racf_id?: string;

  /**
   * Closing the Gap eligibility
   */
  ctg_eligible?: boolean;

  /**
   * Patient's indigenous status
   */
  indigenous_type?:
    | 'aboriginal'
    | 'torres_strait_islander'
    | 'both'
    | 'neither'
    | 'not_stated';
}

export interface IParchmentGetPrescriptionsRequest {
  organisationRef: DocumentReference<IOrganisation>;
  patientRef: DocumentReference<IPatient>;
}

export interface IParchmentGetPrescriptionsSuccess {
  status: 'success';
  data: IPatientPrescriptionSummary[];
  message: string;
}

export interface IParchmentGetPrescriptionsError {
  status: string;
  message: string;
  details?: unknown;
}

export type IParchmentGetPrescriptionsResponse =
  | IParchmentGetPrescriptionsSuccess
  | IParchmentGetPrescriptionsError;

export interface IParchmentPrescriptionApiResponse {
  patient: {
    given_name: string; // 'John'
    family_name: string; // 'Smith'
    date_of_birth: string; // '1990-01-15T00:00:00.000Z'
    partner_patient_id: string; // 'PARTNER#12345'
  };
  prescriber: {
    given_name: string; // 'Dr. Emily'
    family_name: string; // 'Johnson'
  };
  prescriptions: {
    url: string; // 'https://app.parchment.health/prescriptions/rx_abc123'
    scid: string; // '12345678'
    created_date: string; // '2024-01-10T14:30:00'
    item_name: string; // 'Amoxicillin 500mg capsules'
    quantity: string; // '21'
    number_of_repeats_authorised: string; // '0'
    private_prescription: string; // false
  }[];
}
